import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  Link as MuiLink,
} from '@mui/material';
import dayjs from 'dayjs';
import PaymentStatusChip from '../PaymentStatusChip';
import { formatCurrency } from '../../utils/money';
import type { PagamentoInscritosLiga } from '../../types/pagamentoInscritosLiga';

const formatDate = (value?: string | null) =>
  value ? (dayjs(value).isValid() ? dayjs(value).format('DD/MM/YYYY HH:mm') : '') : '';

const DetailRow: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <Box display="flex" justifyContent="space-between" alignItems="center" py={0.5}>
    <Typography variant="body2" color="text.secondary" fontWeight={500}>
      {label}
    </Typography>
    <Typography variant="body2" component="div">
      {children}
    </Typography>
  </Box>
);

export interface BoletoDetailDialogProps {
  open: boolean;
  pagamento: PagamentoInscritosLiga | null;
  onClose: () => void;
}

const BoletoDetailDialog: React.FC<BoletoDetailDialogProps> = ({
  open,
  pagamento,
  onClose,
}) => {
  const hasTransactionInfo =
    pagamento?.invoiceId || pagamento?.codigoTransacao || pagamento?.reciboUrl;

  const isCancelled = pagamento?.status === 'CANCELADO';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Detalhes do Boleto</DialogTitle>
      <DialogContent dividers>
        {pagamento && (
          <>
            {/* Identification */}
            <DetailRow label="Categoria">{pagamento.categoriaTorneio.nome}</DetailRow>
            {pagamento.categoriaTorneio.torneio?.nome && (
              <DetailRow label="Torneio">{pagamento.categoriaTorneio.torneio.nome}</DetailRow>
            )}
            <DetailRow label="Liga">{pagamento.liga.nome}</DetailRow>

            <Divider sx={{ my: 1 }} />

            {/* Values */}
            <DetailRow label="Valor">{formatCurrency(pagamento.valor)}</DetailRow>
            <DetailRow label="Inscritos">{pagamento.quantidadeInscritos}</DetailRow>
            <DetailRow label="Status">
              <PaymentStatusChip status={pagamento.status} />
            </DetailRow>

            <Divider sx={{ my: 1 }} />

            {/* Dates */}
            {pagamento.dataVencimento && (
              <DetailRow label="Vencimento">{formatDate(pagamento.dataVencimento)}</DetailRow>
            )}
            {pagamento.dataPagamento && (
              <DetailRow label="Pagamento">{formatDate(pagamento.dataPagamento)}</DetailRow>
            )}
            {pagamento.criadoEm && (
              <DetailRow label="Criado em">{formatDate(pagamento.criadoEm)}</DetailRow>
            )}

            {/* Transaction Details */}
            {hasTransactionInfo && (
              <>
                <Divider sx={{ my: 1 }} />
                {pagamento.invoiceId && (
                  <DetailRow label="Invoice ID">{pagamento.invoiceId}</DetailRow>
                )}
                {pagamento.codigoTransacao && (
                  <DetailRow label="Código Transação">
                    <Typography variant="body2" fontFamily="monospace">
                      {pagamento.codigoTransacao}
                    </Typography>
                  </DetailRow>
                )}
                {pagamento.reciboUrl && (
                  <DetailRow label="Recibo">
                    <MuiLink
                      href={pagamento.reciboUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                    >
                      Ver boleto
                    </MuiLink>
                  </DetailRow>
                )}
              </>
            )}

            {/* Cancellation Details */}
            {isCancelled && (
              <>
                <Divider sx={{ my: 1 }} />
                {pagamento.motivoCancelamento && (
                  <DetailRow label="Motivo Cancelamento">
                    {pagamento.motivoCancelamento}
                  </DetailRow>
                )}
                {pagamento.dataCancelamento && (
                  <DetailRow label="Data Cancelamento">
                    {formatDate(pagamento.dataCancelamento)}
                  </DetailRow>
                )}
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default BoletoDetailDialog;
